import React, { useState, useEffect } from "react";
import axios from "axios";
import { Copy, Check, Share2, Users, Zap, MapPin, ChevronRight, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitePage() {
  const { user } = useAuth();
  const showToast = useToast();
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    axios.get(`${API}/invites/my-link`, { withCredentials: true })
      .then(r => setInviteData(r.data))
      .catch(() => showToast("Failed to load invite link."));
  }, [user, navigate, showToast]);

  const handleCopy = () => {
    if (!inviteData?.invite_url) return;
    navigator.clipboard.writeText(inviteData.invite_url).then(() => {
      setCopied(true);
      showToast("Invite link copied!");
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleShare = async () => {
    if (!inviteData?.invite_url) return;
    const sport = user?.sports?.[0]?.sport || "sports";
    const city = user?.city || "your city";
    const shareData = {
      title: "Play with me on ActivitySports",
      text: `I'm looking for ${sport} partners in ${city}. Join me on ActivitySports!`,
      url: inviteData.invite_url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        handleCopy();
      }
    } catch { /* cancelled */ }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const r = await axios.post(`${API}/invites/regenerate`, {}, { withCredentials: true });
      const base = inviteData.invite_url.split("/register")[0];
      setInviteData(prev => ({
        ...prev,
        invite_code: r.data.invite_code,
        invite_url: `${base}/register?ref=${r.data.invite_code}`,
      }));
      showToast("Invite link regenerated.");
    } finally {
      setRegenerating(false);
    }
  };

  const sport = user?.sports?.[0]?.sport || "tennis";
  const city = user?.city || "Fort Worth, TX";

  return (
    <div className="min-h-screen bg-brand-navy pt-20 pb-16 px-4 dark-section" data-testid="invite-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-green/15 border border-brand-green/30 mb-4">
            <Users size={24} className="text-brand-green" />
          </div>
          <h1 className="text-4xl font-heading font-black text-white mb-2">Invite Players</h1>
          <p className="text-gray-400 text-sm max-w-sm mx-auto">
            Share your personal invite link. Friends who sign up via your link are tracked, and you'll get notified when they join.
          </p>
        </div>

        {/* Invite link card */}
        <div className="bg-[#1A1F2E] border border-gray-200/10 rounded-2xl p-6 mb-6 dark-section">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={14} className="text-brand-green" />
            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Your Personal Invite Link</span>
          </div>

          {inviteData ? (
            <>
              <div className="flex items-center gap-2 bg-[#0F1219] border border-gray-200/10 rounded-xl px-4 py-3 mb-4 dark-section">
                <span className="flex-1 text-sm text-gray-300 truncate font-mono" data-testid="invite-url">
                  {inviteData.invite_url}
                </span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-brand-green text-white text-sm font-bold rounded-xl hover:bg-brand-green/90 transition-colors"
                  data-testid="copy-invite-btn"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? "Copied!" : "Copy Link"}
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-200/20 text-gray-300 text-sm font-bold rounded-xl hover:border-brand-green/40 hover:text-white transition-colors"
                  data-testid="share-invite-btn"
                >
                  <Share2 size={14} /> Share
                </button>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200/10">
                <div className="text-xs text-gray-500">
                  <span className="text-white font-bold">{inviteData.signups}</span> friend{inviteData.signups !== 1 ? "s" : ""} joined via your link
                </div>
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-50"
                  data-testid="regenerate-invite-btn"
                >
                  <RefreshCw size={11} className={regenerating ? "animate-spin" : ""} /> Reset link
                </button>
              </div>
            </>
          ) : (
            <div className="h-20 flex items-center justify-center text-gray-500 text-sm">Loading…</div>
          )}
        </div>

        {/* Shareable card */}
        <div className="bg-gradient-to-br from-brand-green/10 to-brand-orange/5 border border-brand-green/20 rounded-2xl p-6 mb-6">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-medium">Availability Card</p>
          <div className="bg-[#0F1219] rounded-xl p-4 mb-4 border border-gray-200/10 dark-section">
            <p className="text-white font-bold text-sm leading-relaxed">
              "I'm looking for a <span className="text-brand-green capitalize">{sport}</span> partner in{" "}
              <span className="text-brand-orange">{city}</span> this week.
              Join me on ActivitySports!"
            </p>
            <p className="text-gray-500 text-xs mt-2 font-mono">{inviteData?.invite_url}</p>
          </div>
          <button
            onClick={handleShare}
            className="w-full flex items-center justify-center gap-2 py-2.5 border border-brand-green/30 text-brand-green text-sm font-bold rounded-xl hover:bg-brand-green/10 transition-colors"
            data-testid="share-card-btn"
          >
            <Share2 size={14} /> Share This Card
          </button>
        </div>

        {/* City/Sport links */}
        <div className="bg-[#1A1F2E] border border-gray-200/10 rounded-2xl p-5 dark-section">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-4 font-medium">City & Sport Pages to Share</p>
          <div className="space-y-2">
            {[
              { label: `Find ${sport} partners in ${city}`, href: `/us/${city.toLowerCase().replace(", tx", "").replace(" ", "-")}` },
              { label: "Find Partners near me", href: "/find-partners" },
              { label: "ActivitySports — Sign Up Free", href: "/register" },
            ].map(({ label, href }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between px-4 py-3 bg-[#0F1219] border border-gray-200/10 rounded-xl text-sm text-gray-300 hover:text-white hover:border-brand-green/30 transition-colors group dark-section"
              >
                <span className="flex items-center gap-2">
                  <MapPin size={12} className="text-brand-green" /> {label}
                </span>
                <ChevronRight size={12} className="text-gray-600 group-hover:text-gray-300 transition-colors" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
